import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getCharge, getChargeProof, mapPaymentError } from '../../api/endpoints/payments';
import { usePaymentProof } from './usePaymentProof';
import { useApproveCharge } from './hooks';
import { ReviewPartnerChargeDialog } from './ReviewPartnerChargeDialog';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';
export function ReviewPartnerChargeFlow({
  chargeId,
  isManager,
  invoiceIssued,
  onClose,
}: {
  chargeId: number;
  isManager: boolean;
  invoiceIssued?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const charge = useQuery({
    queryKey: ['payments', 'charge', chargeId],
    queryFn: () => getCharge(chargeId),
  });
  const proof = usePaymentProof(
    chargeId,
    Boolean(charge.data?.proofAvailable),
    false,
    false,
    getChargeProof,
  );
  const approve = useApproveCharge();
  if (!charge.data)
    return (
      <div className={styles.scrim}>
        <section className={styles.dialog} role="dialog" aria-label={t('payments.reviewPartnerChargeDialog.title')}>
          <div className={styles.body}>
            {charge.isError ? (
              <p role="alert">
                {mapPaymentError(charge.error)}
                <Button variant="secondary" size="sm" onClick={() => void charge.refetch()}>
                  {t('payments.reviewPartnerChargeFlow.retry')}
                </Button>
              </p>
            ) : (
              <p role="status">{t('payments.reviewPartnerChargeFlow.loading')}</p>
            )}
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.close')}
            </Button>
          </div>
        </section>
      </div>
    );
  if (charge.data.status !== 'PENDING_APPROVAL')
    return (
      <div className={styles.scrim}>
        <section className={styles.dialog} role="dialog" aria-label={t('payments.reviewPartnerChargeDialog.title')}>
          <div className={styles.body}>
            <p role="alert">{t('payments.reviewPartnerChargeFlow.alreadyDecided')}</p>
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('common.actions.close')}
            </Button>
          </div>
        </section>
      </div>
    );
  return (
    <ReviewPartnerChargeDialog
      charge={{
        id: chargeId,
        requestedPartnerAmount: charge.data.requestedPartnerAmount ?? charge.data.partnerAmount,
        partnerAmount: charge.data.partnerAmount,
        shipperAmount: charge.data.shipperAmount,
        requestedByName: charge.data.requestedByName ?? null,
        description: charge.data.requestedReason ?? charge.data.description,
        proofUrl: proof.url,
      }}
      proofFeedback={
        charge.data.proofAvailable && proof.state !== 'ready' ? (
          <div>
            {proof.state === 'loading' ? (
              <p role="status">{t('payments.reviewPartnerChargeFlow.loadingAttachment')}</p>
            ) : (
              <>
                <p role="alert">{t('payments.reviewPartnerChargeFlow.attachmentUnavailable')}</p>
                <Button variant="secondary" size="sm" onClick={() => void proof.load()}>
                  {t('payments.reviewPartnerChargeFlow.retryAttachment')}
                </Button>
              </>
            )}
          </div>
        ) : undefined
      }
      isManager={isManager}
      invoiceIssued={invoiceIssued}
      onClose={onClose}
      onApprove={async (request) => {
        try {
          await approve.mutateAsync({
            chargeId,
            request: {
              partnerAmount: request.partnerAmount,
              shipperAmount: request.shipperAmount,
              ...(request.adjustmentNote ? { note: request.adjustmentNote } : {}),
            },
          });
          onClose();
        } catch (cause) {
          throw new Error(mapPaymentError(cause));
        }
      }}
    />
  );
}
